import { resolveSourceMedia, type SourceHeaderPicture, type SourceHeaderVideo, type SourceMediaMountData } from '../source-media/core';

export interface SourceCardProductType { id: string; name: string; }

export interface SharedSourceCard {
	source_id: string;
	name: string;
	href: string;
	pictures?: SourceHeaderPicture[];
	videos?: SourceHeaderVideo[];
	media?: SourceMediaMountData;
	product_types: SourceCardProductType[];
	is_private: boolean;
	release: string;
	price_label: string;
	price_class: string;
	text: string;
}

export interface SourceCardRenderOptions {
	privateIcon?: string;
	defaultCoverBase?: string;
	colClass?: string;
}

export function renderSourceCardElement(card: SharedSourceCard, options: SourceCardRenderOptions = {}): HTMLElement {
	const col = document.createElement('div');
	col.className = options.colClass || 'col-12 col-sm-6 col-lg-3 d-flex align-items-start px-2 py-2 mb-2';

	const mediaData = card.media || {};
	const pictures = card.pictures || mediaData.pictures || [];
	const videos = card.videos || mediaData.videos || [];
	const defaultCoverBase = options.defaultCoverBase || mediaData.defaultCoverBase || '';
	const media = resolveSourceMedia(pictures, videos, defaultCoverBase);
	const isVideo = media.kind === 'video' && !!media.src;
	const cardEl = document.createElement('div');
	cardEl.className = 'card text-left w-100 shadow-sm d-flex flex-column ft-source-catalog-card ft-source-catalog-card--dtr border border-secondary border-opacity-25'
		+ (isVideo ? ' video-card-hover' : '');
	cardEl.setAttribute('data-bs-theme', 'dark');

	const mediaLink = document.createElement('a');
	mediaLink.href = card.href;
	mediaLink.className = 'ft-source-catalog-card__media d-flex align-items-center justify-content-center bg-dark flex-shrink-0 w-100 position-relative text-decoration-none';
	mediaLink.style.cssText = 'overflow: hidden; min-height: 5rem; max-height: 22rem;';
	mediaLink.setAttribute('aria-label', 'View ' + (card.name || 'source'));
	const mediaStyle = 'max-width: 100%; max-height: 22rem; width: 100%; height: auto; object-fit: contain; vertical-align: top;';
	if (isVideo) {
		const video = document.createElement('video');
		video.className = 'w-100 d-block';
		video.style.cssText = mediaStyle;
		video.src = media.src;
		video.muted = true;
		video.setAttribute('playsinline', '');
		video.setAttribute('preload', 'metadata');
		if (media.poster) video.setAttribute('poster', media.poster);
		mediaLink.appendChild(video);
	} else {
		const img = document.createElement('img');
		img.className = 'w-100 d-block';
		img.style.cssText = mediaStyle;
		img.src = media.src;
		img.alt = '';
		img.loading = 'lazy';
		img.decoding = 'async';
		mediaLink.appendChild(img);
	}
	cardEl.appendChild(mediaLink);

	const facts = document.createElement('a');
	facts.href = card.href;
	facts.className = 'ft-source-catalog-card__facts d-flex flex-column flex-grow-1 text-decoration-none text-white shadow-none';
	facts.tabIndex = -1;
	facts.setAttribute('aria-hidden', 'true');

	const body = document.createElement('div');
	body.className = 'card-body py-2 px-3';
	const title = document.createElement('h4');
	title.className = 'card-title fs-6 lh-sm mb-0 fw-semibold text-white';
	title.textContent = card.name || 'Untitled';
	body.appendChild(title);

	if (card.product_types?.length) {
		const badges = document.createElement('div');
		badges.className = 'd-flex flex-wrap gap-1 mt-2';
		badges.setAttribute('aria-label', 'Product types');
		for (const pt of card.product_types) {
			if (!pt.name) continue;
			const b = document.createElement('span');
			b.className = 'badge bg-secondary bg-opacity-75 fw-normal text-wrap';
			b.textContent = pt.name;
			badges.appendChild(b);
		}
		body.appendChild(badges);
	}

	if (card.is_private) {
		const priv = document.createElement('span');
		priv.className = 'badge bg-warning text-dark mt-1';
		if (options.privateIcon) priv.innerHTML = options.privateIcon + ' ';
		priv.appendChild(document.createTextNode('Private'));
		body.appendChild(priv);
	}
	facts.appendChild(body);

	const footer = document.createElement('div');
	footer.className = 'card-footer ft-source-catalog-card__footer d-flex align-items-center justify-content-between gap-2 px-3 py-2 border-top border-0 border-opacity-25';
	const price = document.createElement('span');
	price.className = 'ft-source-catalog-card__price fw-semibold lh-sm';
	const priceInner = document.createElement('span');
	priceInner.className = card.price_class || '';
	priceInner.textContent = card.price_label || '';
	price.appendChild(priceInner);
	footer.appendChild(price);
	if (card.release) {
		const rel = document.createElement('span');
		rel.className = 'ft-source-catalog-card__release text-muted small lh-sm text-end';
		rel.textContent = card.release;
		footer.appendChild(rel);
	}
	facts.appendChild(footer);

	cardEl.appendChild(facts);
	col.appendChild(cardEl);
	return col;
}
